import { useCallback, useEffect, useRef, useState } from 'react';
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import { usePowerSyncDb } from '../adapters/powersync';

/**
 * Upload queue stats (PRD §7.2).
 *
 * `count` / `hasPending` are kept for backward compatibility with existing
 * callers; the richer fields (size / oldest / per-table) are added for the
 * Sync Rescue flow.
 */
export interface UploadQueueInfo {
  count: number;
  hasPending: boolean;
  estimatedSizeBytes: number;
  oldestPendingAt: string | null;
  tables: Record<string, number>;
}

const EMPTY: UploadQueueInfo = {
  count: 0,
  hasPending: false,
  estimatedSizeBytes: 0,
  oldestPendingAt: null,
  tables: {},
};

const POLL_INTERVAL_MS = 2_000;

// PowerSync's internal CRUD queue is exposed as the `ps_crud` table. Each row
// is `{ id, tx_id, data }` where `data` is the JSON-encoded CrudEntry. We read
// it directly so we can compute size / oldest / per-table breakdowns that the
// high-level getUploadQueueStats() does not provide.
interface PsCrudRow {
  id: number;
  tx_id: number | null;
  data: string;
}

/**
 * Inspect the pending upload queue without mutating it.
 *
 * Safe to call repeatedly — reading `ps_crud` does not complete or remove any
 * operations (only a successful upload transaction does).
 */
export async function getUploadQueueStats(db: AbstractPowerSyncDatabase): Promise<UploadQueueInfo> {
  // Total pending count via the supported high-level API.
  let count = 0;
  try {
    const stats = await db.getUploadQueueStats();
    count = stats.count;
  } catch {
    // fall through — we may still be able to read ps_crud below
  }

  let estimatedSizeBytes = 0;
  // PowerSync op ids are monotonic but not timestamps; we don't have a reliable
  // per-op wall-clock here, so oldestPendingAt stays null on this SDK version.
  const oldestPendingAt: string | null = null;
  const tables: Record<string, number> = {};

  try {
    const rows = await db.getAll<PsCrudRow>(`SELECT id, tx_id, data FROM ps_crud ORDER BY id ASC`);
    if (rows.length > count) count = rows.length;
    for (const row of rows) {
      estimatedSizeBytes += row.data ? row.data.length : 0;
      try {
        const parsed = JSON.parse(row.data) as { type?: string };
        const table = parsed.type ?? 'unknown';
        tables[table] = (tables[table] ?? 0) + 1;
      } catch {
        tables.unknown = (tables.unknown ?? 0) + 1;
      }
    }
  } catch {
    // ps_crud not readable on this SDK version — keep the high-level count.
  }

  return {
    count,
    hasPending: count > 0,
    estimatedSizeBytes,
    oldestPendingAt,
    tables,
  };
}

/**
 * React hook: poll upload queue stats every 2s.
 */
export function useUploadQueueStats(): UploadQueueInfo {
  const { db } = usePowerSyncDb();
  const [info, setInfo] = useState<UploadQueueInfo>(EMPTY);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    if (!db) return;
    try {
      setInfo(await getUploadQueueStats(db));
    } catch {
      // Silently ignore — stats are best-effort
    }
  }, [db]);

  useEffect(() => {
    if (!db) return;

    void poll();
    intervalRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [db, poll]);

  return info;
}
